import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  Booking,
  BookingChange,
  DriverDetailInterface,
  newGuideInterface,
} from './models';
import { Timestamp } from '@angular/fire/firestore';
import { getISOWeek, getISOWeekYear } from 'date-fns';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { v4 as uuidv4 } from 'uuid';

export function patchBookingForm(
  bookingForm: FormGroup,
  booking: Booking,
  fb: FormBuilder,
): void {
  if (!booking) return;

  bookingForm.patchValue({
    id: booking.id,
    shipId: booking.ship?.id, // Assuming `ship` has an `id` property
    createdDate: new Date(booking.createdDate.seconds * 1000).toISOString(),
    userCreatorId: booking.userCreatorId,
    userCreatorName: booking.userCreatorName,
    // lastEditDate: new Date(booking.lastEditDate.seconds * 1000).toISOString(),
    //userLastEditorId: booking.userLastEditorId,
    // userLastEditorName: booking.userLastEditorName,
    pdfUrl: booking.pdfUrl || '',
  });

  // Patch `infoDetails` group
  const cruiseDetails = booking.cruiseDetails;

  bookingForm.get('cruiseDetails')?.patchValue({
    jetty: cruiseDetails?.jetty || '',
    cruiseManager: cruiseDetails?.cruiseManager || '',
    cruiseManagerTel: cruiseDetails?.cruiseManagerTel || '',
    paxShip: cruiseDetails?.paxShip || '',
    paxTour: cruiseDetails?.paxTour || '',
    internNote: cruiseDetails?.internNote || '',
    invoiceNumber: cruiseDetails?.invoiceNumber || '',
  });

  // Patch `duration` group
  const durationDetails = booking.duration;
  bookingForm.get('duration')?.patchValue({
    date:
      new Date(durationDetails.startTime.seconds * 1000)
        .toISOString()
        .split('T')[0] || '',
    startTime: formatUnixToTimeString(durationDetails.startTime.seconds),
    endTime: formatUnixToTimeString(durationDetails.endTime.seconds),
    tourCode: durationDetails.tourCode || '',
  });

  const busInfo = booking.busInfos;
  // Patch `busInfo` group
  const busInfoGroup = bookingForm.get('busInfo');
  if (busInfoGroup && busInfo) {
    (busInfoGroup.get('busSelected') as FormArray).clear();
    busInfoGroup.get('busCompany')?.patchValue(busInfo.busCompany);

    Object.entries(busInfo?.selectedBuses).forEach(([key, val]) => {
      (busInfoGroup.get('busSelected') as FormArray).push(
        generateBus({ type: key, amount: val }, fb),
      );
    });
  }

  const driverDetails = booking.driverInfo;

  bookingForm.get('driverInfo')?.patchValue({
    url: driverDetails?.url || '',
    customerNumber: driverDetails
      ? driverDetails.customerNumber.toString()
      : '',
    orderNumber: driverDetails ? driverDetails.orderNumber.toString() : '',
  });

  const driverInfoArray = (bookingForm.get('driverInfo') as FormGroup).get(
    'drivers',
  ) as FormArray;

  driverInfoArray.clear();
  if (driverDetails?.drivers) {
    Object.values(driverDetails.drivers).forEach((driver) => {
      driverInfoArray.push(generateDriverForm(fb, driver));
    });
  }
  // Patch `guides` array
  const guidesArray = bookingForm.get('guides') as FormArray;
  guidesArray.clear();
  if (booking.guides && booking.guideOrder) {
    booking.guideOrder.forEach((guideID) => {
      guidesArray.push(generateGuideForm(fb, booking.guides![guideID]));
    });
  } else if (booking.guides) {
    Object.values(booking.guides).forEach((guide) => {
      guidesArray.push(generateGuideForm(fb, guide));
    });
  }

  /*
    const sortedGuides = Object.values(booking.guides).sort((a, b) => {
      const aActive = a.storno?.isActive ?? false;
      const bActive = b.storno?.isActive ?? false;

      // Move active storno to the end
      return Number(aActive) - Number(bActive);
    });

    sortedGuides.forEach((guide) => {
      guidesArray.push(generateGuideForm(fb, guide));
    });
  }
*/

  (bookingForm.get('voucher') as FormGroup)
    .get('addonText')
    ?.patchValue(booking.voucherAddonText);

  (bookingForm.get('rundown') as FormGroup)
    .get('text')
    ?.patchValue(booking.rundown);

  const accountingGroup = bookingForm.get('accounting') as FormGroup;

  accountingGroup.patchValue({
    note: booking.accounting?.accountingNote ?? booking.accountingNote ?? '',
    billed: booking.accounting?.billing?.billed ?? false,
    dateBilled: booking.accounting?.billing?.dateBilled ?? null,
    userBilledId: booking.accounting?.billing?.userBilledId ?? '',
    userBilledName: booking.accounting?.billing?.userBilledName ?? '',
  });
}

function generateDriverForm(
  fb: FormBuilder,
  driver: DriverDetailInterface,
): FormGroup {
  return fb.group({
    driverName: [driver?.driverName || ''],
    driverTel: [driver?.driverTel || ''],
  });
}

function generateGuideForm(
  fb: FormBuilder,
  guide: newGuideInterface,
): FormGroup {
  let languagesFA = fb.array([]) as FormArray;
  let otherFA = fb.array([]) as FormArray;
  Object.entries(guide.guideDetails.languages).forEach(([key, val]) => {
    languagesFA.push(generateLanguageForm({ name: key, isActive: !!val }, fb));
  });
  Object.entries(guide.other).forEach(([key, val]) => {
    otherFA.push(generateOtherForm(val, fb));
  });

  return fb.group({
    guideDetails: fb.group({
      id: [guide.guideDetails.id],
      forename: [guide.guideDetails.forename],
      surname: [guide.guideDetails.surname],
      tel: [guide.guideDetails.tel],
      email: [guide.guideDetails.email],
      languages: languagesFA,
    }),
    positionDetails: fb.group({
      id: [guide.positionDetails.id],
      time: [
        formatUnixToTimeString(guide.positionDetails.time.seconds),
        Validators.required,
      ],
      location: [guide.positionDetails.location],
    }),
    tour: fb.group({
      id: [guide.tour.id],
      tourInfo: fb.group({
        id: [guide.tour.tourInfo.id],
        tourName: [guide.tour.tourInfo.tourName || guide.tour.tourInfo],
        colorCode: [guide.tour.tourInfo.colorCode || ''],
      }),
      tourLanguage: [guide.tour.tourLanguage],
      tourConfirm: [guide.tour.tourConfirm],
    }),
    storno: fb.group({
      storno: [guide.storno.storno],
      customStorno: [guide.storno.customStorno],
      isActive: [guide.storno.isActive],
    }),
    other: otherFA,
  });
}

function generateBus(
  selectedBus: { type: string; amount: number },
  fb: FormBuilder,
): FormGroup {
  return fb.group({
    type: [selectedBus.type],
    amount: [selectedBus.amount],
  });
}

function generateLanguageForm(
  lang: { name: string; isActive: boolean },
  fb: FormBuilder,
): FormGroup {
  return fb.group({
    name: [lang.name],
    isActive: [lang.isActive],
  });
}

function generateOtherForm(text: string, fb: FormBuilder): FormGroup {
  return fb.group({
    text: text,
  });
}

export function convertFormToBooking(
  form: FormGroup,
  loadedBooking: Booking,
): Booking {
  const value = form.value;

  let cruiseManagerTel: string = '';
  if (value.cruiseDetails.cruiseManagerTel) {
    const cruiseMangerTelForm =
      value.cruiseDetails.cruiseManagerTel.internationalNumber;
    // Format the phone number
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parse(cruiseMangerTelForm, 'ZZ');
    cruiseManagerTel = phoneUtil.format(
      number,
      PhoneNumberFormat.INTERNATIONAL,
    );
  }

  const drivers = patchDriverTel(
    (form.get('driverInfo') as FormGroup).get('drivers') as FormArray,
  );

  const guideOrder: string[] = createGuideOrder(value.guides);

  return {
    ...loadedBooking,
    id: value.id || undefined,
    ship: loadedBooking.ship,
    createdDate: Timestamp.fromDate(new Date(value.createdDate)),
    userCreatorId: value.userCreatorId,
    userCreatorName: value.userCreatorName,
    pdfUrl: value.pdfUrl,
    busInfos: value.busInfo
      ? {
          busCompany: value.busInfo.busCompany || '-',
          selectedBuses: (value.busInfo.busSelected || []).reduce(
            (
              acc: Record<string, number>,
              bus: { type: string; amount: number },
            ) => {
              acc[bus.type] = bus.amount;

              return acc;
            },
            {},
          ),
        }
      : undefined,
    duration: {
      date:
        stringToFirestoreTimestamp(
          value.duration.date,
          value.duration.startTime,
        ) || Timestamp.fromDate(new Date()),
      startTime:
        stringToFirestoreTimestamp(
          value.duration.date,
          value.duration.startTime,
        ) || Timestamp.fromDate(new Date()),
      endTime:
        stringToFirestoreTimestamp(
          value.duration.date,
          value.duration.endTime,
        ) || Timestamp.fromDate(new Date()),

      tourCode: value.duration.tourCode,
    },

    driverInfo: value.driverInfo
      ? {
          url: value.driverInfo.url || '',
          customerNumber: parseInt(value.driverInfo.customerNumber, 10),
          orderNumber: parseInt(value.driverInfo.orderNumber, 10),
          drivers: Object.keys(drivers).length > 0 ? drivers : [], // Only assign if not empty
        }
      : undefined,
    guides: convertFormToGuide(value.guides),
    guideOrder: guideOrder,
    guideIds: guideOrder,
    cruiseDetails: value.cruiseDetails
      ? {
          jetty: value.cruiseDetails.jetty || '',
          cruiseManager: value.cruiseDetails.cruiseManager || '',
          cruiseManagerTel: cruiseManagerTel || '',
          paxShip: parseInt(value.cruiseDetails.paxShip, 10),
          paxTour: parseInt(value.cruiseDetails.paxTour, 10),
          internNote: value.cruiseDetails.internNote || '',
          invoiceNumber: value.cruiseDetails.invoiceNumber || '',
        }
      : undefined,
    rundown: value.rundown.text || '',
    voucherAddonText: value.voucher.addonText || '',
    voucher: loadedBooking.voucher || null,

    accounting: {
      id: value.accounting.id || uuidv4(),
      accountingNote: value.accounting.note || '',
      billing: {
        billed: value.accounting.billed ?? false,
        dateBilled: value.accounting.dateBilled ?? null,
        userBilledId: value.accounting.userBilledId ?? '',
        userBilledName: value.accounting.userBilledName ?? '',
      },
    },
  };
}

function patchDriverTel(
  drivers: FormArray,
): Record<number, DriverDetailInterface> {
  const driversMap: Record<number, DriverDetailInterface> = {};
  const phoneUtil = PhoneNumberUtil.getInstance();

  drivers.controls.forEach((driver, index) => {
    const tel = driver.get('driverTel')?.value?.internationalNumber;
    const name = driver.get('driverName')?.value ?? 'Unknown'; // Fallback if name is undefined

    let formattedTel = tel ?? ''; // Fallback if tel is undefined

    if (tel) {
      try {
        const number = phoneUtil.parse(tel, 'ZZ');
        formattedTel = phoneUtil.format(
          number,
          PhoneNumberFormat.INTERNATIONAL,
        );
      } catch (error) {
        console.warn(`Invalid phone number for driver ${index}: ${tel}`);
      }
    }

    driversMap[index] = { driverName: name, driverTel: formattedTel };
  });

  return driversMap;
}

export function sortGuides(
  guides: Record<string, newGuideInterface>,
  guidesOrder: string[],
): newGuideInterface[] {
  const sortedGuides: newGuideInterface[] = [];
  guidesOrder.forEach((guideId) => {
    sortedGuides.push(guides[guideId]);
  });

  return sortedGuides;
}

// Function to convert date and time strings to Firestore Timestamp
export function stringToFirestoreTimestamp(
  dateStr: string,
  timeStr: string,
): Timestamp {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Create a Date object in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

  return Timestamp.fromDate(utcDate); // Firestore stores in UTC by default
}

function createGuideOrder(guidesFormArray: newGuideInterface[]) {
  const orderArray: string[] = [];
  guidesFormArray.forEach((guide) => {
    orderArray.push(guide.guideDetails.id);
  });
  return orderArray;
}

function convertFormToGuide(
  guidesFormArray: newGuideInterface[],
): Record<string, newGuideInterface> {
  let guides: Record<string, newGuideInterface> = {};

  guidesFormArray.forEach((guide) => {
    if (guide?.guideDetails?.languages) {
      let languages: Record<string, boolean> = {};

      // Ensure languages is an array and correctly map it to the Record<string, boolean>
      if (Array.isArray(guide.guideDetails.languages)) {
        guide.guideDetails.languages.forEach((lang) => {
          if (lang.name) {
            languages[lang.name] = !!lang.isActive;
          }
        });
      }

      let other: Record<string, string> = {};
      if (Array.isArray(guide.other)) {
        if (guide.other.length > 0) {
          guide.other.forEach((oth, index) => {
            if (oth) {
              other[index] = oth.text;
            }
          });
        }
      }
      guide.other = other;
      //position Time need the any to convert it to string
      const timeString = guide.positionDetails.time as any as string;
      const today = new Date();
      const [hours, minutes] = timeString.split(':');
      const timeDate = new Date(
        today.setUTCHours(Number(hours), Number(minutes), 0, 0),
      ); // Set hours and minutes, and reset seconds and milliseconds
      guide.positionDetails.time = Timestamp.fromDate(timeDate);

      // Now assign the languages safely
      guide.guideDetails.languages = languages;

      // Add the guide to the guides record
      guides[guide.guideDetails.id] = guide;
    }
  });

  return guides;
}

export function formatUnixToTimeString(unixTimestamp: number): string {
  const date = new Date(unixTimestamp * 1000); // Convert seconds to milliseconds
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// The UTC calendar day of a booking instant as a local-midnight Date, so date-fns (startOfWeek,
// isSameDay, format) and the DatePipe see the same day in every browser timezone.
export function utcCalendarDay(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

//Get calendar week in YYYYWww format
export function getCalendarWeek(date: string | Date): string {
  const d = new Date(date);
  // Booking times are wall-clock-in-UTC (see stringToFirestoreTimestamp), so the week comes from the
  // UTC calendar date; the browser's local date would push Sunday-night bookings into the next week.
  const utcDay = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const isoWeek = getISOWeek(utcDay);
  const isoYear = getISOWeekYear(utcDay);

  // Format it as YYYY'W'WW and test
  return `${isoYear}W${isoWeek.toString().padStart(2, '0')}`;
}

export function getLatestChange(booking: Booking): BookingChange | undefined {
  return booking.changes?.reduce((latest, current) =>
    latest.timestamp.toMillis() > current.timestamp.toMillis()
      ? latest
      : current,
  );
}

export function extractHashFromFilename(url: string): string {
  // Find either 'booking-' or 'voucher-' in the URL
  const bookingIndex = url.indexOf('booking-');
  const voucherIndex = url.indexOf('voucher-');

  let startIndex: number;
  let prefix: string;

  // Determine which pattern exists in the URL
  if (bookingIndex !== -1) {
    startIndex = bookingIndex;
    prefix = 'booking-';
  } else if (voucherIndex !== -1) {
    startIndex = voucherIndex;
    prefix = 'voucher-';
  } else {
    return ''; // Neither pattern found
  }

  // Extract the part starting with the identified prefix
  const relevantPart = url.substring(startIndex);

  // Find where the query parameters or other parts start
  const endMarkers = ['?', '#', ' '];
  let endIndex = relevantPart.length;

  for (const marker of endMarkers) {
    const idx = relevantPart.indexOf(marker);
    if (idx !== -1 && idx < endIndex) {
      endIndex = idx;
    }
  }

  // Extract just the filename without query parameters
  const filename = relevantPart.substring(0, endIndex);

  // Now get the UUID part
  // Remove prefix (booking- or voucher-)
  const withoutPrefix = filename.substring(prefix.length);

  // Check if there's a timestamp (indicated by another dash after the UUID)
  const lastDashIndex = withoutPrefix.lastIndexOf('-');
  const lastDotIndex = withoutPrefix.lastIndexOf('.');

  // If there's a dash and it's before any file extension
  if (
    lastDashIndex !== -1 &&
    (lastDotIndex === -1 || lastDashIndex < lastDotIndex)
  ) {
    // There's probably a timestamp - extract just the UUID
    return withoutPrefix.substring(0, lastDashIndex);
  } else {
    // No timestamp - check if there's a file extension to remove
    if (lastDotIndex !== -1) {
      return withoutPrefix.substring(0, lastDotIndex);
    }
    return withoutPrefix; // Return the whole string after prefix
  }
}
